import { useEffect, useState } from 'react'
import { formatDate, formatDateYMD, penceToCurrency, STATUS, stringToPence, wait } from '../../helpers/utils';
import { differenceInCalendarDays, format } from 'date-fns'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCheck, faTimes, faFileImport, faWarning, faFlask, faCircleXmark, faFloppyDisk } from '@fortawesome/free-solid-svg-icons'
import { Loader } from '../../helpers/loader';
import { supabase } from '../../api'
import ELNStore, { addProducts, updateCategories, updateShops, updateSuppliers } from '../../store';
import { LogItem } from '../ImportLog';


var Strings = {
    /**
     * Wrapped CSV line parser
     * @param s      String delimited CSV string
     * @param sep    Separator override
     * @attribution: http://www.greywyvern.com/?post=258 (comments closed on blog :( )
     */
    parseCSV: function (s, sep) {
        // http://stackoverflow.com/questions/1155678/javascript-string-newline-character
        var universalNewline = /\r\n|\r|\n/g;
        var a = s.split(universalNewline);
        for (var i in a) {

            if (typeof a[i] !== 'function') {
                for (var f = a[i].split(sep = sep || ","), x = f.length - 1, tl; x >= 0; x--) {
                    if (f[x].replace(/"\s+$/, '"').charAt(f[x].length - 1) === '"') {
                        if ((tl = f[x].replace(/^\s+"/, '"')).length > 1 && tl.charAt(0) === '"') {
                            f[x] = f[x].replace(/^\s*"|"\s*$/g, '').replace(/""/g, '"');
                        } else if (x) {
                            f.splice(x - 1, 2, [f[x - 1], f[x]].join(sep));
                        } else f = f.shift().split(sep).concat(f);
                    } else f[x].replace(/""/g, '"');
                } a[i] = f;
            }
        }
        return a;
    }
}

export const Importer = ({ currentUser, testRun }) => {

    const [data, setData] = useState([])

    const suppliers = ELNStore.useState(s => s.suppliers);
    const categories = ELNStore.useState(s => s.categories);
    const shops = ELNStore.useState(s => s.shops);
    const products = ELNStore.useState(s => s.products);
    
    const [loading, setLoading] = useState(false)

    const [fileName, setFileName] = useState('')

    const [log, setLog] = useState([])

    const [fileRoot, setFileRoot] = useState('')

    const DO_SAVE = !testRun

    const resetAll = () => {
        setData([])
        setLog([])
        setLoading(false)
        setFileName('')
    }

    const doLog = (status, message, recordType, recordID, beforeData, afterData) => {
        setLog(prev => {

            const existing = prev.find(p => p.recordID === recordID && p.message === message)
            if (existing) return prev
            return [
                ...prev,
                {
                    status,
                    message,
                    recordType,
                    recordID,
                    beforeData,
                    afterData
                }
            ]
        })
    }

    const getFileRoot = (fileName) => {
        const dt = new Date()
        return `/orders/${formatDateYMD(dt)}/${format(dt,'HH:mm:ss')}-${currentUser.id}/${fileName.split('.')[0]}`
    }

    const txtToDate = (txt) => {
        const dtp = txt.split('/')
        return new Date(`${dtp[2]}/${dtp[1]}/${dtp[0]}`)
    }

    useEffect(() => {
        resetAll()
    // eslint-disable-next-line react-hooks/exhaustive-deps
    },[testRun])

    useEffect(() => {
        if (!loading) {
            handleFinish()
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    },[loading])


    const handleFinish = async () => {

        if (testRun) return /* dont save log for test run */

        console.log('FINISHED')

        const file = new File(
            [JSON.stringify(log, null, 2)], // pretty optional
            "data.json",
            { type: "application/json" }
        );

        const { data2, error2 } = await supabase
        .storage
        .from('imports')
        .upload(`${fileRoot}.json`, file, {
            cacheControl: '3600',
            upsert: true,
        })
    }
    
    const processAll = async (e) => {
        const content = e.target.result;
        const data = Strings.parseCSV(content)
        setData(data)

        const savedProducts = await processProducts(data, products, suppliers, categories)

        savedProducts && addProducts(savedProducts)
 

        await processOrders(data, savedProducts ? [...products, ...savedProducts] : [...products], shops)
 
        await wait(50)

        setLoading(false)

    }

    const handleChangeFile = async (file) => {


        const fileRoot = getFileRoot(file.name)
        setFileRoot(fileRoot)

        if (!testRun) {
            const { data, error } = await supabase
            .storage
            .from('imports')
            .upload(`${fileRoot}.csv`, file, {
                cacheControl: '3600',
                upsert: true
            })

            if (error) console.error(error)
        }

        setLoading(true)
        setFileName(file.name)
         await wait(100)



        let fileData = new FileReader();
        fileData.onloadend = processAll;
        fileData.readAsText(file);
    }

    const getCol = (row, name) => {
        return row.findIndex(col => col === name)
    }

    if (loading) return (
        <div className='import-wrapper'><Loader /></div>
    )

    const processProducts = async (data, products, suppliers, categories) => {
        const nameCol = getCol(data[0], 'Product Description')
        const descCol = getCol(data[0], 'Pack Description')
        const pidCol = getCol(data[0], 'PID')
        const unitCost = getCol(data[0], 'PO UnitCost')
        const supplierCol = getCol(data[0], 'Supplier')
        const deliveryDateCol = getCol(data[0], 'Delivered Date')
            
        const productsInImport = {}

        const newProducts = []
        let savedProducts = []

        for (const [i, row] of data.entries()) {
            
            if (i === 0) continue
            if (!row[pidCol]) continue

            const dt = txtToDate(row[deliveryDateCol])

            const dtDiff = differenceInCalendarDays(dt, new Date())
            if (dtDiff <= 0) continue /* don't run products for orders in past */

            const existingProduct = products.find(p => parseInt(p.pid) === parseInt(row[pidCol]))

            const supplier = suppliers.find(s => s.name === row[supplierCol])
            const category = categories.find(c => row[nameCol].includes(c.name))

            if (row[pidCol]) productsInImport[row[pidCol]] = {
                name: row[nameCol],
                desc: row[descCol],
                category: category,
                supplierName: row[supplierCol],
                supplier: supplier,
                existingProduct: existingProduct,
                unitCost: row[unitCost],
            }
        }

        for (var row of Object.entries(productsInImport)) {

            const product = row[1]

            if (!product.existingProduct) newProducts.push({
                pid: parseInt(row[0]),
                name: product.name,
                details: product.desc,
                category_id: product.category?.id || null,
                supplier_id: product.supplier?.id || null,
                unit_cost: stringToPence(product.unitCost),
                active: true,
            })

        }

        // save new products now
        if (newProducts.length) {
            let status = STATUS.CHANGES
            if (DO_SAVE) {
                const { data, error } = await supabase
                    .from('products')
                    .insert(newProducts)
                    .select('*, categories (*)')
                if (error) {
                    status = STATUS.ERR
                }
                else {
                    savedProducts = data
                }
            }
            else {
                savedProducts = newProducts
            }
            doLog(status,`CREATE ${newProducts.length} new product(s)`,'P', undefined, [], newProducts)
        }
       
        for (const row of Object.entries(productsInImport)) {

            const product = row[1]

            if (!product.existingProduct) continue  /* only for existing products */
            
            const pid = parseInt(row[0])
            const oldData = { unit_cost: product.existingProduct.unit_cost }
            const newData = { unit_cost: stringToPence(product.unitCost) }

            const priceIsDifferent = product.existingProduct ? product.existingProduct.unit_cost !== newData.unit_cost : false 

            if (!priceIsDifferent) continue

            let status = STATUS.CHANGES
            if (DO_SAVE) {
                const { data, error } = await supabase
                    .from('products')
                    .update(newData)
                    .match({ pid: pid })
                    .select()

                if (error) {
                    status = STATUS.ERR
                    
                }
            }

            if (status === STATUS.CHANGES) product.priceChanged = true
            doLog(status, `UPDATE price for ${product.name}: ${penceToCurrency(oldData.unit_cost)} to ${penceToCurrency(newData.unit_cost)}`,'P', pid)
            await wait(5) 
        }

        return savedProducts
    }


    const processOrders = async (data, products, shops) => {
        const orderStatusCol = getCol(data[0], 'Order Status')
        const deliveryDateCol = getCol(data[0], 'Delivered Date')
        const shopCol = getCol(data[0], 'Purchaser Unit')
        const pidCol = getCol(data[0], 'PID')
        const poNumCol = getCol(data[0], 'OrderID')
        const quantity = getCol(data[0], 'PO QTY')
        const unitCost = getCol(data[0], 'PO UnitCost')

        const orders = {}

        // orders

        const rowRef = (row) => {
            return row[poNumCol]
        }

        for (const [i, row] of data.entries()) {

            if (i === 0) continue
            if (!row[shopCol]) continue
            if (row[orderStatusCol] !== 'Awaiting Delivery') {
                doLog(STATUS.WARN,`Unexpected order status: "${row[orderStatusCol]}"`,'O',row[poNumCol])
                continue
            }

            const shopObj = shops.find(s => s.import_as === row[shopCol])

            const dt = txtToDate(row[deliveryDateCol])
            const dtDiff = differenceInCalendarDays(dt, new Date())

            if (dtDiff <= 0) {
                doLog(STATUS.WARN, `Delivery date ${formatDate(dt)} is in the PAST. Order not processed`,'O',row[poNumCol])
            }
            else if (!shopObj) {
                doLog(STATUS.WARN, `Unexpected shop "${row[shopCol]}". Order not processed`,'O',row[poNumCol])
            }
            else {
                orders[rowRef(row)] = {
                    shop_name: row[shopCol],
                    shop_id: shopObj.id,
                    delivery_dt: row[deliveryDateCol],
                    dt: dt,
                    products: [],
                    purchase_order_num: rowRef(row),
                }
            }

            await wait(5) 

        }

        for (const [i, row] of data.entries()) {

            if (i === 0) continue
            if (!row[shopCol]) continue
            if (!orders[rowRef(row)]) continue


            //still match on PID as we've just created some new records 
            const product = products.find(p => parseInt(p.pid) === parseInt(row[pidCol]))

            if (product) {
                const orderProduct = {
                    pid: row[pidCol],
                    product: product,
                    quantity: parseInt(row[quantity]),
                    unit_cost: stringToPence(row[unitCost]),
                }

                const duplicateProduct = orders[rowRef(row)].products.find(p => parseInt(p.pid) === parseInt(row[pidCol]))

                if (duplicateProduct) duplicateProduct.quantity += parseInt(row[quantity])
                else orders[rowRef(row)].products.push(orderProduct)

            }

            await wait(5) 

        }

        const orderArray = Object.entries(orders)

        //save orders
        for (let index = 0; index < orderArray.length; index++) {
            await saveOrder(fileName, orderArray[index][1])
            await wait(5)
        }

        return orders
    }

    const generateOrderProducts = (products, orderID, forTestRun) => {
        return products.map(op => {
            
            const obj = {
            order_id: orderID,
            product_id: op.product.id,
            quantity: op.quantity,
            quantity_imported: op.quantity,
            unit_cost: op.unit_cost,
            }
            if (forTestRun) {
                obj.testRun_product = op.product
            }
            return obj
        })
    }

    const saveOrder = async (fileName, order) => {

        if (!order.shop_id) {
            order.saved = false;
            return;
        }

        let oldOrder = undefined

        const data = {
            import_file: fileName,
            imported_at: new Date(),
            imported_by: currentUser.id,
            shop_id: order.shop_id,
            purchase_order_num: order.purchase_order_num,
            delivery_dt: format(order.dt, 'dd-MMM-yyyy')
        }

        // delete existing orders

        const { data: existingOrders, error: existingErr } = await supabase
            .from('orders')
            .select('*, orderProducts (quantity, unit_cost, quantity_imported, product_id, note)')
            .eq('purchase_order_num', order.purchase_order_num)
            .match({ shop_id: order.shop_id, delivery_dt: format(order.dt,'dd-MMM-yyyy') })

        if (existingErr) console.error(existingErr)

        if (existingOrders?.length) {
            oldOrder = existingOrders[0]
        }

        if (oldOrder) {
            
            data.id = oldOrder.id 
            data.notes = oldOrder.notes

            if (oldOrder.voided_at) {
                doLog(STATUS.WARN,`Order is already voided. Order not processed`,'O', data.purchase_order_num)
                order.saved = false
                return 
            }

            if (oldOrder.approved_at) {
                doLog(STATUS.WARN,`Order is already APPROVED.  Order not processed`,'O', data.purchase_order_num)
                order.saved = false
                return
            }

            if (oldOrder.is_altered) {
                doLog(STATUS.WARN,`Order has been changed manually in B2B.  Order not processed`,'O', data.purchase_order_num)
                order.saved = false
                return
            }

        }
        else {
            // new order record
            data.created_by = currentUser.id
            data.created_at = new Date()
            
        }


        let savedOrder = undefined
        let changesMade = false 


        let status = STATUS.OK

        let orderProducts = []
        
        if (DO_SAVE) {
            const { data: orderSaved, error: orderError } = await supabase
                .from('orders')
                .upsert(
                    data
                )
                .select()
                .single()

            if (orderError) {
                status = STATUS.ERR
            }
            savedOrder = orderSaved
            
        }
        else {
            savedOrder = data
        }

        if (status === STATUS.OK) {

            orderProducts = generateOrderProducts(order.products, savedOrder?.id || '?')
            
            if (DO_SAVE) {

                if (oldOrder) { /* delete previous orderProducts records */
                    const { data: deleteExistingOP, error: delOPErr } = await supabase
                        .from('orderProducts')
                        .delete()
                        .eq('order_id', oldOrder.id)
                }

                const { data2, error2 } = await supabase
                    .from('orderProducts')
                    .insert(orderProducts)

                if (error2) {
                    status = STATUS.ERR
                }

            }
            else {
                orderProducts = generateOrderProducts(order.products, savedOrder?.id || '?', true)
            }

            // look for changes
            if (oldOrder && oldOrder.orderProducts) {
                orderProducts.forEach(op => {
                    const prevOp = oldOrder.orderProducts.find(oop => oop.product_id === op.product_id)
                    if (prevOp) {
                        if (prevOp.quantity !== op.quantity) {
                            op.quantityDiff = true 
                            changesMade = true 
                        }
                        if (prevOp.unit_cost !== op.unit_cost) {
                            op.costDiff = true 
                            changesMade = true
                        }
                    }
                    else {
                        changesMade = true
                    }
                })
                if (orderProducts.length !== oldOrder.orderProducts.length) changesMade = true 
            }

            savedOrder = { ...savedOrder, orderProducts, changesMade }
        }

        if (status === STATUS.OK && (changesMade || !oldOrder)) status = STATUS.CHANGES 

        doLog(status, oldOrder ? `UPDATE order - ${changesMade ? 'CHANGES' : 'NO CHANGES'}` : 'CREATE NEW order','O',data.purchase_order_num, oldOrder, savedOrder)

    }


    if (data.length) {

        return (
            <div className='import-wrapper'>
                <h1 style={testRun ? {background:'#ff0', padding: 16} : {}} >{testRun ? <><FontAwesomeIcon icon={faFlask} />&nbsp;TEST RUN - showing expected changes only</> : 'IMPORT - CHANGES WILL BE SAVED'}...</h1><br/>
                <div className='center'>{fileName}</div>

                <br /><br />
                {!loading && log.length ? log.map(l => <LogItem item={l} />) : ''}
                <br/><br/>
                
                

                <br/>
                <h1 style={testRun ? {background:'#ff0', padding: 16} : {}}>{testRun ? <><FontAwesomeIcon icon={faFlask} />&nbsp;TEST RUN - showing expected changes only - import has not run!</> : 'IMPORT FINISHED - CHANGES HAVE BEEN SAVED'}...</h1><br/>
                {!loading && <>
                
                    <button onClick={resetAll} className='clear'><FontAwesomeIcon icon={faTimes} />&nbsp;CLOSE REPORT AND UPLOAD AGAIN</button>
                    <br/>

                    <br/>
                    {!testRun && <h3><FontAwesomeIcon icon={faWarning} /> To update data on other pages please <button onClick={() => window.location.reload()}>reload the app now</button></h3>    }
                    
                </>
                }
            </div>
        )
    }

    return (
            <div className='import-wrapper'>
                <div className='file-upload-wrapper'>
                    <label tabIndex={0} className='file-upload-button' htmlFor='file-upload'><FontAwesomeIcon icon={testRun ? faFlask : faFileImport} />&nbsp;&nbsp;{testRun ? 'TEST RUN' : 'IMPORT'} PW Orders </label>
                    <input id='file-upload' type="file" accept=".csv" onChange={e => handleChangeFile(e.target.files[0])} />
                </div>
            </div>
    )

}

export default Importer;
